import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { db } from "@lib/firebase";
import { collection, query, where, getDocs, doc, getDoc, orderBy } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "@mmfestival/shared-components/common/NotificationContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faCheckCircle,
  faClock,
  faCalendar,
  faExchangeAlt,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";
import { CategoryTransferView } from "@mmfestival/shared-components/common/CategoryTransferView";
import { InputFieldProfile, InputField, Button } from "@mmfestival/shared-components";

const API_BASE = import.meta.env.VITE_API_BASE_URL;

const cachePesananData = (uid, data) => {
  try {
    // Keep storage key same for backward compatibility
    localStorage.setItem(
      `pesanan_${uid}`,
      JSON.stringify({ data, timestamp: Date.now() })
    );
  } catch (err) {
    console.warn("Failed to save cache:", err);
  }
};

const getCachedPesananData = (uid) => {
  try {
    const cached = localStorage.getItem(`pesanan_${uid}`);
    if (!cached) return null;
    const { data, timestamp } = JSON.parse(cached);
    const cacheDuration = 5 * 60 * 1000; // 5 minutes
    if (Date.now() - timestamp > cacheDuration) {
      localStorage.removeItem(`pesanan_${uid}`);
      return null;
    }
    if (!data || !Array.isArray(data.pesanan)) {
      console.warn("Invalid cache: pesanan is not an array");
      localStorage.removeItem(`pesanan_${uid}`);
      return null;
    }
    return data;
  } catch (err) {
    console.warn("Failed to read cache:", err);
    localStorage.removeItem(`pesanan_${uid}`);
    return null;
  }
};

const shortenLocation = (location) => {
  if (!location || typeof location !== "string") return "-";
  const parts = location.split(" ");
  if (parts.length === 1) return location.slice(0, 8);
  if (parts[0].toLowerCase().startsWith("jakarta")) {
    return `Jkt ${parts[1] || ""}`.trim();
  }
  return parts[0].slice(0, 4);
};

export const MyOrders = React.memo(() => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [pesanan, setPesanan] = useState(() => {
    const cached = getCachedPesananData(user?.uid);
    return cached ? cached.pesanan : [];
  });
  const [suratPernyataan, setSuratPernyataan] = useState(() => {
    const cached = getCachedPesananData(user?.uid);
    return cached ? cached.suratPernyataan : {};
  });
  const [parentConsent, setParentConsent] = useState(() => {
    const cached = getCachedPesananData(user?.uid);
    return cached ? cached.parentConsent : {};
  });
  const [eventTitles, setEventTitles] = useState(() => {
    const cached = getCachedPesananData(user?.uid);
    return cached ? cached.eventTitles : {};
  });
  const [eventConfigs, setEventConfigs] = useState({}); // New state for configs
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [transaction, setTransaction] = useState(null);
  const [searchParams] = useSearchParams();
  const [activeMenu, setActiveMenu] = useState("All Orders");
  const [notificationShown, setNotificationShown] = useState(false);
  const isMounted = useRef(true);
  const fetchStatus = useRef({ isFetching: false });

  // Category Transfer Modal State
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);

  const orderId = searchParams.get("order_id");
  const status = searchParams.get("transaction_status");

  const hasEligibleTransfer = useMemo(() => {
    return pesanan.some(order => {
      const config = eventConfigs[order.eventId];
      if (!config) return false;
      const enable = config.enabled === true;
      const pDate = (d) => {
        if (!d) return null;
        if (d.includes("-")) {
          const date = new Date(d);
          date.setHours(23, 59, 59, 999);
          return date;
        }
        if (d.includes("/")) {
          const [day, month, year] = d.split("/");
          const date = new Date(`${year}-${month}-${day}T23:59:59`);
          return date;
        }
        return new Date(d);
      };

      const deadlinePassed = config.deadline ? new Date() > pDate(config.deadline) : false;
      return enable && !deadlinePassed;
    });
  }, [pesanan, eventConfigs]);

  const menus = useMemo(() => {
    return [
      { name: "All Orders", icon: faCalendar },
      { name: "Pending", icon: faClock },
      { name: "Completed", icon: faCheckCircle },
    ];
  }, []);

  const formatDate = useCallback((date) => {
    if (!date) return "Not available";
    try {
      // Support Firestore Timestamp, strings and Date objects
      if (date && (date.seconds || date._seconds)) {
        const timestamp = date.seconds || date._seconds;
        return new Date(timestamp * 1000).toLocaleDateString("en-US", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        });
      }
      if (date.toDate && typeof date.toDate === "function") {
        return date.toDate().toLocaleDateString("en-US", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        });
      }
      if (typeof date === "string") {
        let parsedDate = new Date(date.replace(" at ", " "));
        if (!isNaN(parsedDate)) {
          return parsedDate.toLocaleDateString("en-US", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          });
        }
        parsedDate = new Date(date);
        if (!isNaN(parsedDate)) {
          return parsedDate.toLocaleDateString("en-US", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          });
        }
      }
      if (date instanceof Date && !isNaN(date)) {
        return date.toLocaleDateString("en-US", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        });
      }
      console.warn("Unrecognized date format:", date);
      return "Not available";
    } catch (err) {
      console.error("Failed to format date:", err, date);
      return "Not available";
    }
  }, []);

  const fetchPesananDanSurat = useCallback(
    async (forceRefresh = false) => {
      if (!user?.uid || fetchStatus.current.isFetching) {
        if (!user?.uid) setError("User not authenticated.");
        setLoading(false);
        return;
      }

      fetchStatus.current.isFetching = true;
      try {
        setLoading(true);
        setError("");

        if (!forceRefresh) {
          const cachedData = getCachedPesananData(user.uid);
          if (cachedData && isMounted.current) {
            setPesanan(cachedData.pesanan);
            setSuratPernyataan(cachedData.suratPernyataan);
            setParentConsent(cachedData.parentConsent || {});
            setEventTitles(cachedData.eventTitles || {});

            // Fetch Configs for cached events
            if (cachedData.pesanan?.length > 0) {
              const uniqueEventIds = [...new Set(cachedData.pesanan.map(p => p.eventId))];
              uniqueEventIds.forEach(id => {
                 if(id) {
                    axios.get(`${API_BASE}/api/category-transfer/config/${id}`)
                    .then(({data}) => setEventConfigs(prev => ({...prev, [id]: data})))
                    .catch(err => console.error("Config fetch error", err));
                 }
              });
            }

            setLoading(false);
            return;
          }
        }

        const pesananList = [];
        const processedOrderIds = new Set();
        const suratData = {};
        const eventTitlesData = {};
        const eventTypes = {};

        // semuaKegiatan -> build titles & types
        try {
          const eventsSnapshot = await getDocs(collection(db, "semuaKegiatan"));
          for (const eventDoc of eventsSnapshot.docs) {
            const eventId = eventDoc.id;
            const eventData = eventDoc.data();
            eventTitlesData[eventId] = eventData.title || eventId;
            eventTypes[eventId] = eventData.type || "unknown";
          }
        } catch (err) {
          console.warn("Failed to fetch semuaKegiatan:", err);
        }

        // registrations per event
        try {
          const eventsSnapshot = await getDocs(collection(db, "semuaKegiatan"));
          for (const eventDoc of eventsSnapshot.docs) {
            const eventId = eventDoc.id;
            const eventData = eventDoc.data();
            const collectionName =
              eventData.collectionName || `pendaftaran${eventId}`;
            try {
              const pesananQuery = query(
                collection(db, collectionName),
                where("uid", "==", user.uid),
                where("transaction_status", "in", ["pending", "settlement"])
              );
              const pesananSnapshot = await getDocs(pesananQuery);
              pesananSnapshot.forEach((d) => {
                const data = d.data();
                if (!processedOrderIds.has(d.id)) {
                  const isSeminar = eventTypes[eventId] === "seminar";
                  pesananList.push({
                    id: d.id,
                    eventId,
                    collectionName,
                    kategori: isSeminar ? null : data.kategori || null,
                    type: "event",
                    ...data,
                  });
                  processedOrderIds.add(d.id);
                }
              });
            } catch (err) {
              console.warn(
                `Failed to fetch registrations for ${eventId}:`,
                err
              );
            }
          }
        } catch (err) {
          console.warn(
            "Failed iterating semuaKegiatan for registrations:",
            err
          );
        }

        // shuttle (JOTR)
        try {
          const shuttleQuery = query(
            collection(db, "pendaftaranShuttle"),
            where("uid", "==", user.uid),
            where("eventId", "==", "JOTR"),
            where("transaction_status", "in", ["pending", "settlement"])
          );
          const shuttleSnapshot = await getDocs(shuttleQuery);
          shuttleSnapshot.forEach((d) => {
            const data = d.data();
            if (!processedOrderIds.has(d.id)) {
              pesananList.push({
                id: d.id,
                eventId: "JOTR",
                collectionName: "pendaftaranShuttle",
                pickupPoint: data.pickupPoint || null,
                departureTime: data.departureTime || null,
                returnPoint: data.returnPoint || null,
                returnTime: data.returnTime || null,
                type: "shuttle",
                transaction_status: data.transaction_status || "pending",
                harga: data.harga || 0,
                createdAt: data.createdAt || null,
                email: data.email || null,
                telepon: data.telepon || null,
                alamat: data.alamat || null,
                namaLengkap: data.namaLengkap || null,
                snapToken: data.snapToken || null,
                orderId: data.orderId || d.id,
              });
              processedOrderIds.add(d.id);
            }
          });
        } catch (err) {
          console.error("Failed to fetch pendaftaranShuttle:", err);
        }

        // orderMappings -> map to real collection docs
        try {
          const orderMappingsQuery = query(
            collection(db, "orderMappings"),
            where("uid", "==", user.uid)
          );
          const orderMappingsSnapshot = await getDocs(orderMappingsQuery);
          for (const orderDoc of orderMappingsSnapshot.docs) {
            const orderData = orderDoc.data();
            const mappedOrderId = orderData.orderId;
            const eventId = orderData.eventId;
            if (processedOrderIds.has(mappedOrderId)) continue;
            const eventDocRef = doc(db, "semuaKegiatan", eventId);
            const eventDoc = await getDoc(eventDocRef);
            const collectionName = eventDoc.exists()
              ? eventDoc.data().collectionName || `pendaftaran${eventId}`
              : `pendaftaran${eventId}`;
            const registrationDocRef = doc(db, collectionName, mappedOrderId);
            const registrationDoc = await getDoc(registrationDocRef);
            if (registrationDoc.exists()) {
              const registrationData = registrationDoc.data();
              if (
                registrationData.uid === user.uid &&
                ["pending", "settlement"].includes(
                  registrationData.transaction_status
                )
              ) {
                const isSeminar = eventTypes[eventId] === "seminar";
                pesananList.push({
                  id: registrationDoc.id,
                  eventId,
                  collectionName,
                  kategori: isSeminar
                    ? null
                    : registrationData.kategori || null,
                  type: "event",
                  ...registrationData,
                });
                processedOrderIds.add(mappedOrderId);
              }
            }
            // also check shuttle doc mapped to this orderId
            const shuttleDocRef = doc(db, "pendaftaranShuttle", mappedOrderId);
            const shuttleDoc = await getDoc(shuttleDocRef);
            if (shuttleDoc.exists()) {
              const shuttleData = shuttleDoc.data();
              if (
                shuttleData.uid === user.uid &&
                ["pending", "settlement"].includes(
                  shuttleData.transaction_status
                ) &&
                shuttleData.eventId === "JOTR"
              ) {
                pesananList.push({
                  id: shuttleDoc.id,
                  eventId: "JOTR",
                  collectionName: "pendaftaranShuttle",
                  pickupPoint: shuttleData.pickupPoint || null,
                  departureTime: shuttleData.departureTime || null,
                  returnPoint: shuttleData.returnPoint || null,
                  returnTime: shuttleData.returnTime || null,
                  type: "shuttle",
                  transaction_status:
                    shuttleData.transaction_status || "pending",
                  harga: shuttleData.harga || 0,
                  createdAt: shuttleData.createdAt || null,
                  email: shuttleData.email || null,
                  telepon: shuttleData.telepon || null,
                  alamat: shuttleData.alamat || null,
                  namaLengkap: shuttleData.namaLengkap || null,
                  snapToken: shuttleData.snapToken || null,
                  orderId: shuttleData.orderId || mappedOrderId,
                });
                processedOrderIds.add(mappedOrderId);
              }
            }
          }
        } catch (err) {
          console.warn("Failed to process orderMappings:", err);
        }

        // suratPernyataan
        try {
          const suratQuery = query(
            collection(db, "suratPernyataan"),
            where("userId", "==", user.uid)
          );
          const suratSnapshot = await getDocs(suratQuery);
          suratSnapshot.forEach((d) => {
            const data = d.data();
            const eventId = data.eventId;
            if (eventId && data.userId === user.uid) {
              const existing = suratData[eventId];
              if (
                !existing ||
                (existing.createdAt &&
                  data.createdAt &&
                  data.createdAt.seconds > existing.createdAt.seconds)
              ) {
                suratData[eventId] = {
                  downloadURL: data.downloadURL,
                  fileName: data.fileName,
                  namaLengkap: data.namaLengkap,
                  createdAt: data.createdAt,
                };
              }
            }
          });
        } catch (err) {
          console.warn("Failed to fetch suratPernyataan:", err);
        }

        // Fetch parentConsent documents
        const parentConsentData = {};
        try {
          const parentConsentQuery = query(
            collection(db, "parentConsent"),
            where("userId", "==", user.uid)
          );
          const parentConsentSnapshot = await getDocs(parentConsentQuery);
          parentConsentSnapshot.forEach((d) => {
            const data = d.data();
            const eventId = data.eventId;
            if (eventId && data.userId === user.uid) {
              const existing = parentConsentData[eventId];
              if (
                !existing ||
                (existing.createdAt &&
                  data.createdAt &&
                  data.createdAt.seconds > existing.createdAt.seconds)
              ) {
                parentConsentData[eventId] = {
                  downloadURL: data.downloadURL,
                  fileName: data.fileName,
                  namaLengkap: data.namaLengkap,
                  kategori: data.kategori,
                  participantAge: data.participantAge,
                  createdAt: data.createdAt,
                };
              }
            }
          });
        } catch (err) {
          console.warn("Failed to fetch parentConsent:", err);
        }

        if (isMounted.current) {
          setPesanan(pesananList);
          setSuratPernyataan(suratData);
          setParentConsent(parentConsentData);
          setEventTitles(eventTitlesData);
          
           const uniqueEventIds = [...new Set(pesananList.map(p => p.eventId))];
           uniqueEventIds.forEach(id => {
              if(id) {
                 axios.get(`${API_BASE}/api/category-transfer/config/${id}`)
                 .then(({data}) => setEventConfigs(prev => ({...prev, [id]: data})))
                 .catch(err => console.error("Config fetch error", err));
              }
           });

          cachePesananData(user.uid, {
            pesanan: pesananList,
            suratPernyataan: suratData,
            parentConsent: parentConsentData,
            eventTitles: eventTitlesData,
          });
        }
      } catch (err) {
        if (isMounted.current) {
          setError(
            err.code === "permission-denied"
              ? "Access denied. Please ensure you have permission to access this data."
              : err.code === "unavailable"
              ? "Server connection failed. Check your network."
              : "Failed to load order data. Please try again."
          );
          showNotification(
            "error",
            err.code === "permission-denied"
              ? "Access denied. Please ensure you have permission."
              : err.code === "unavailable"
              ? "Connection failed. Check your network."
              : "Failed to load orders."
          );
        }
      } finally {
        if (isMounted.current) setLoading(false);
        fetchStatus.current.isFetching = false;
      }
    },
    [user, showNotification]
  );

  const fetchTransaction = useCallback(async () => {
    if (!orderId || fetchStatus.current.isFetching) return;
    fetchStatus.current.isFetching = true;
    try {
      const res = await axios.get(
        `${import.meta.env.VITE_API_BASE_URL}/api/payment/status/${orderId}`
      );
      if (res.data && isMounted.current) {
        setTransaction(res.data);
      }
    } catch (error) {
      console.error("Failed to fetch transaction status:", error);
      if (isMounted.current) {
        setError(
          error.response?.status === 403
            ? "API access denied. Check server configuration."
            : error.response?.status === 404
            ? "Order ID not found."
            : "Failed to fetch transaction data."
        );
        showNotification(
          "error",
          error.response?.status === 403
            ? "API access denied."
            : error.response?.status === 404
            ? "Order ID not found."
            : "Failed to fetch transaction data."
        );
      }
    } finally {
      fetchStatus.current.isFetching = false;
    }
  }, [orderId, showNotification]);

  const handleBayar = useCallback(
    (snapToken) => {
      if (snapToken && window.snap) {
        window.snap.pay(snapToken, {
          onSuccess: () => {
            showNotification("success", "Payment successful!");
            fetchPesananDanSurat(true);
          },
          onPending: () => {
            showNotification("warning", "Please continue the payment.");
            fetchPesananDanSurat(true);
          },
          onError: () => {
            setError("An error occurred during payment.");
            showNotification("error", "An error occurred during payment.");
          },
          onClose: () => {},
        });
      } else {
        setError("Payment token or Midtrans not found.");
        showNotification("error", "Payment token or Midtrans not found.");
      }
    },
    [showNotification, fetchPesananDanSurat]
  );

  useEffect(() => {
    isMounted.current = true;
    if (!user) {
      navigate("/login");
      showNotification("error", "Please log in to view your orders.");
      return;
    }

    fetchPesananDanSurat(true);

    if (status && !notificationShown) {
      if (status === "settlement") {
        showNotification("success", "Payment successful!");
        setNotificationShown(true);
      } else if (status === "failed") {
        showNotification("error", "Payment failed.");
        setNotificationShown(true);
      }
    }

    if (orderId) {
      fetchTransaction();
    }

    return () => {
      isMounted.current = false;
    };
  }, [user, navigate, orderId, status, showNotification, fetchTransaction]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredPesanan = useMemo(() => {
    if (!Array.isArray(pesanan)) {
      console.warn("pesanan is not an array:", pesanan);
      return [];
    }
    return pesanan.filter((item) => {
      if (activeMenu === "All Orders") return true;
      if (activeMenu === "Pending")
        return item.transaction_status === "pending";
      if (activeMenu === "Completed")
        return item.transaction_status === "settlement";
      return true;
    });
  }, [pesanan, activeMenu]);

  const getEventName = (item) => {
    return eventTitles[item.eventId] || item.eventId || "Unknown";
  };

  // Modal logic removed
  const handleTransferComplete = () => {
    // Clear cache and refresh data
    localStorage.removeItem(`pesanan_${user?.uid}`);
    fetchPesananDanSurat(true);
  };

  return (
    <section className="w-full min-h-screen py-20 bg-white text-slate-700">
      <div className="container mx-auto px-4 md:px-4 max-w-6xl">
        {/* Page Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-slate-800">Order History</h2>
        </div>

        <div className="flex flex-col md:flex-row gap-4 md:items-start">
          {/* Sidebar */}
          <div className="w-full md:w-64 bg-white rounded-xl md:border md:border-slate-200 p-4">
            <ul>
              {menus.map((menu) => (
                <li key={menu.name}>
                  <button
                    onClick={() => {
                      setActiveMenu(menu.name);
                    }}
                    className={`w-full flex items-center gap-2 px-4 py-2 mb-2 text-md font-medium rounded-xl transition-colors duration-200 ${
                      activeMenu === menu.name
                        ? "bg-slate-700 text-white"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                    aria-pressed={activeMenu === menu.name}
                  >
                    <FontAwesomeIcon icon={menu.icon} />
                    {menu.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex-1">
            <div className="space-y-4">
                {/* Header moved to top of page */}

                <AnimatePresence>
                {error && (
                  <div className="bg-red-100 text-red-800 p-4 rounded-xl mb-4 flex items-start gap-2">
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      className="h-5 w-5 mr-2"
                    />
                    <span>{error}</span>
                  </div>
                )}
                </AnimatePresence>

               {/* Desktop: Clean Card Design */}
               <div className="hidden md:block space-y-4">
                {filteredPesanan.map((item) => (
                   <div
                     key={`${item.eventId}-${item.id}`}
                     className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                   >
                     {/* Row 1: Event Info & Price */}
                     <div className="p-6">
                      <div className="flex justify-between items-start mb-6">
                        {/* Event Title & Badge */}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="text-xl font-bold text-slate-900">
                              {getEventName(item)}
                            </h3>
                          </div>
                          <div className="text-sm text-slate-500 font-medium flex items-center gap-1">
                             <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                               <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                             </svg>
                             {item.eventId === "JMM" ? "Patuha, West Java - Indonesia" : item.eventId === "BMM" ? "Kintamani, Bali - Indonesia" : "Event Location"}
                          </div>
                        </div>
                        
                        {/* Price */}
                         <div className="text-right">
                          <span className="block text-2xl font-bold text-slate-900">
                            Rp {item.harga ? item.harga.toLocaleString() : "0"}
                          </span>
                        </div>
                      </div>

                      {/* Participant Name Row */}
                      <div className="mb-4">
                         <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                           Participant Name
                         </span>
                         <span className="text-lg font-bold text-slate-800">
                            {item.namaLengkap || user?.displayName || "-"}
                         </span>
                      </div>

                      {/* Divider */}
                      <div className="h-px bg-slate-100 w-full mb-6"></div>

                      {/* Details Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {/* Order ID */}
                        <div>
                           <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                             Order ID
                           </span>
                           <span className="text-sm font-semibold text-slate-700 break-all">
                              {item.orderId ? item.orderId.replace(/^(ORDER-|MEMBERSHIP-)/, "") : item.id.substring(0, 8)}
                           </span>
                        </div>

                        {/* Category */}
                        <div>
                           <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                             Category
                           </span>
                           <span className="text-sm font-semibold text-slate-700">
                              {item.type === "shuttle"
                                ? "Shuttle Bus"
                                : item.kategori || "-"}
                           </span>
                        </div>

                         {/* Transaction Time */}
                        <div>
                           <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                             Transaction Time
                           </span>
                           <span className="text-sm font-semibold text-slate-700">
                              {formatDate(item.createdAt || item.transaction_time)}
                           </span>
                        </div>

                        {/* BIB Number (Only if paid and available) */}
                        {item.transaction_status === "settlement" && item.bibNumber ? (
                          <div>
                             <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                               BIB Number
                             </span>
                             <span className="text-lg font-black text-slate-800 tracking-wider">
                                {item.bibNumber}
                             </span>
                          </div>
                        ) : (
                          <div>
                            <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                               Status
                             </span>
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                                item.transaction_status === "settlement"
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-amber-100 text-amber-700"
                              }`}
                            >
                              {item.transaction_status === "settlement" ? "Success" : "Pending"}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Row 2: Actions & Documents */}
                    <div className="p-6 bg-slate-50 flex items-center justify-between">
                       {/* Left: Documents */}
                       <div className="flex items-center gap-3">
                          {/* Declaration Letter */}
                          {item.type !== "shuttle" && item.kategori && (
                            <a
                              href={`/SuratPernyataan/${item.eventId}`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white text-slate-700 text-sm font-semibold rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors h-10 min-w-[140px]"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l2.293-2.293a1 1 0 011.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                              Declaration Letter
                            </a>
                          )}
                          
                          {/* Parent Consent */}
                          {parentConsent[item.eventId] && (
                            <a
                              href={parentConsent[item.eventId].downloadURL}
                              download={parentConsent[item.eventId].fileName}
                              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white text-slate-700 text-sm font-semibold rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors h-10 min-w-[140px]"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                              Parent Consent
                            </a>
                          )}
                       </div>

                       {/* Right: View Details Button - Same size/style */}
                       <div>
                          {item.transaction_status === "pending" && item.snapToken && (
                             <button
                               onClick={() => handleBayar(item.snapToken)}
                               className="inline-flex items-center justify-center px-6 py-2 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-colors h-10"
                             >
                               Pay Now
                             </button>
                          )}
                       </div>
                    </div>
                  </div>
                ))}
                {filteredPesanan.length === 0 && !loading && (
                  <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-semibold text-slate-800">No Orders Found</h3>
                      <p className="text-slate-500">You haven't made any purchases yet.</p>
                      <Button
                        onClick={() => navigate("/")}
                        className="mt-2"
                      >
                        Browse Events
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile: card view with status badge top-left and Waiver bottom-right next to price */}
              <div className="md:hidden px-0 space-y-4">
                {filteredPesanan.map((item) => (
                  <div
                    key={`${item.eventId}-${item.id}`}
                    className="bg-white border border-slate-200 rounded-xl overflow-hidden"
                  >
                    <div className="p-5">
                       {/* Header: Title & Status */}
                       <div className="flex justify-between items-start mb-4">
                          <div className="flex-1 mr-4">
                              <h3 className="text-lg font-bold text-slate-900 leading-tight mb-2">
                                {getEventName(item)}
                              </h3>
                              <div className="text-xs text-slate-500 font-medium flex items-center gap-1">
                                 <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                   <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                 </svg>
                                 {item.eventId === "JMM" ? "Patuha, West Java" : item.eventId === "BMM" ? "Kintamani, Bali" : "Location"}
                              </div>
                          </div>
                       </div>

                       {/* Participant Name Row (Mobile) */}
                       <div className="mb-4">
                          <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                            Participant Name
                          </span>
                          <span className="text-sm font-bold text-slate-800">
                             {item.namaLengkap || user?.displayName || "-"}
                          </span>
                       </div>

                       {/* Divider */}
                       <div className="h-px bg-slate-100 w-full mb-4"></div>

                       {/* Details Grid (Mobile: 2 cols) */}
                       <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                             <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
                               Order ID
                             </span>
                             <span className="text-xs font-semibold text-slate-700 break-all">
                                {item.orderId ? item.orderId.replace(/^(ORDER-|MEMBERSHIP-)/, "") : item.id.substring(0, 8)}
                             </span>
                          </div>
                          <div>
                             <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
                               Category
                             </span>
                             <span className="text-xs font-semibold text-slate-700">
                                {item.type === "shuttle" ? "Shuttle" : item.kategori || "-"}
                             </span>
                          </div>
                          <div>
                             <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
                               Date
                             </span>
                             <span className="text-xs font-semibold text-slate-700">
                                {formatDate(item.createdAt || item.transaction_time)}
                             </span>
                          </div>
                          <div>
                            {item.transaction_status === "settlement" && item.bibNumber ? (
                              <>
                                <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
                                 BIB
                               </span>
                               <span className="text-sm font-black text-slate-800 tracking-wider">
                                  {item.bibNumber}
                               </span>
                              </>
                            ) : (
                               <>
                                <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
                                 Status
                               </span>
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                                    item.transaction_status === "settlement"
                                      ? "bg-emerald-100 text-emerald-700"
                                      : "bg-amber-100 text-amber-700"
                                  }`}
                                >
                                  {item.transaction_status === "settlement" ? "Success" : "Pending"}
                                </span>
                               </>
                            )}
                          </div>
                       </div>
                       


                       {/* Documents section */}
                       {(suratPernyataan[item.eventId] || parentConsent[item.eventId]) && (
                         <div className="mt-3 pt-3 border-t border-slate-100 mb-4">
                           <span className="text-xs font-medium text-slate-500 uppercase mb-2 block">
                             Documents
                           </span>
                           <div className="flex flex-wrap gap-2">
                             {item.type !== "shuttle" &&
                               item.kategori &&
                               suratPernyataan[item.eventId] && (
                                 <a
                                   href={`/SuratPernyataan/${item.eventId}`}
                                   target="_blank"
                                   rel="noreferrer"
                                   className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-slate-700 text-xs font-semibold rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors"
                                   aria-label={`Open waiver for ${item.id}`}
                                 >
                                   <svg
                                     xmlns="http://www.w3.org/2000/svg"
                                     className="h-3.5 w-3.5"
                                     viewBox="0 0 20 20"
                                     fill="currentColor"
                                   >
                                     <path
                                       fillRule="evenodd"
                                       d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                                       clipRule="evenodd"
                                     />
                                   </svg>
                                   Declaration Letter
                                 </a>
                               )}
                             {parentConsent[item.eventId] && (
                               <a
                                 href={parentConsent[item.eventId].downloadURL}
                                 download={parentConsent[item.eventId].fileName}
                                 className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-slate-700 text-xs font-semibold rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors"
                                 aria-label={`Download parent consent for ${item.id}`}
                               >
                                 <svg
                                   xmlns="http://www.w3.org/2000/svg"
                                   className="h-3.5 w-3.5"
                                   viewBox="0 0 20 20"
                                   fill="currentColor"
                                 >
                                   <path
                                     fillRule="evenodd"
                                     d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                                     clipRule="evenodd"
                                   />
                                 </svg>
                                 Parent Consent
                               </a>
                             )}
                           </div>
                         </div>
                       )}

                       {/* Price */}
                       <div className="flex justify-between items-center bg-slate-50 -mx-5 -mb-5 px-5 py-3 border-t border-slate-100 mt-2">
                          <div>
                             <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                               Total
                             </span>
                             <span className="block text-lg font-bold text-slate-900">
                               Rp {item.harga ? item.harga.toLocaleString() : "0"}
                             </span>
                          </div>
                          
                          {/* Mobile Action Button */}
                           <div>
                              {item.transaction_status === "pending" && item.snapToken && (
                                 <button
                                   onClick={() => handleBayar(item.snapToken)}
                                   className="inline-flex items-center justify-center px-4 py-2 bg-slate-900 text-white text-xs font-bold rounded-lg hover:bg-slate-800 transition-colors h-9"
                                 >
                                   Pay Now
                                 </button>
                              )}
                           </div>
                       </div>
                    </div>



                    {/* actions */}
                    <div className="mt-4 pt-3 border-t border-slate-100 space-y-2">
                      {item.transaction_status === "pending" && item.snapToken && (
                        <button
                          onClick={() => handleBayar(item.snapToken)}
                          className="w-full px-4 py-2.5 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-colors duration-200"
                          aria-label={`Pay for order ${item.id}`}
                        >
                          Pay Now
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {filteredPesanan.length === 0 && !loading && (
                  <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-500">
                    No orders found.
                  </div>
                )}
              </div>

            </div>

            {/* Always show Category Request at the bottom of All Orders if eligible */}
            {activeMenu === "All Orders" && hasEligibleTransfer && (
              <div className="mt-4">
                <CategoryTransferView 
                  orders={pesanan} 
                  onTransferComplete={handleTransferComplete} 
                  eventConfigs={eventConfigs}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
});

export default MyOrders;
